import type { ColumnPinningState } from '@tanstack/react-table';
import type { CSSProperties } from 'react';

/** The table's column pinning for a caller that pins one column at the right edge, or none. */
export function columnPinningFor(
  pinnedRightColumnId: string | undefined
): ColumnPinningState {
  return { left: [], right: pinnedRightColumnId ? [pinnedRightColumnId] : [] };
}

/**
 * A cell is pinned at the right edge when the table scrolls sideways, with a hairline at its left to read as a separate
 * pane. A row's own colour shows through a cell that has none, so the cell paints the host's surface under the row's tint,
 * and a host with a coloured surface or row states says so through two custom properties:
 *
 * - `--andromeda-pinned-cell-background`: the opaque colour behind the table, `var(--mantine-color-body)` by default.
 * - `--andromeda-pinned-cell-tint`: a colour the row lays over it, such as a hover or selection. Left out, none.
 * - `--andromeda-pinned-header-background`: the opaque colour of the pinned header cell, which is the header band's own
 *   (`--mantine-color-default-hover`) by default, so the band is not broken by a cell of the surface's colour.
 *
 * The hairline at the cell's left is the colour of the table's row separators (`--table-border-color`), so it is no
 * darker than they are.
 *
 * The header's cell sits above the body's, so a body cell that is scrolled under it does not show through.
 */
export function pinnedRightCellStyle(
  part: 'header' | 'body'
): Readonly<CSSProperties> {
  return {
    position: 'sticky',
    right: 0,
    zIndex: part === 'header' ? 2 : 1,
    backgroundColor:
      part === 'header'
        ? 'var(--andromeda-pinned-header-background, var(--mantine-color-default-hover))'
        : 'var(--andromeda-pinned-cell-background, var(--mantine-color-body))',
    backgroundImage:
      'linear-gradient(var(--andromeda-pinned-cell-tint, transparent), var(--andromeda-pinned-cell-tint, transparent))',
    borderLeft:
      '1px solid var(--table-border-color, var(--mantine-color-default-border))',
  };
}
